import { throttledInterval, clearThrottled } from './utils.js';
import { EXHAUST_COLORS } from '../../shared/bean-physics.js';
export { fireFireworks } from '../../shared/fireworks.js';

export { EXHAUST_COLORS };

// ─── Reusable typewriter ──────────────
// Any ongoing typeText session dispatches `bean-type-start` / `bean-type-end`
// events on document. Bean animation code listens for these to drive the
// paw-wiggle animator — so any call to typeText anywhere on the page
// automatically makes Bean's paws animate.
const typeTimers = new Map();
const typingElements = new Set();

function emitTypingStart(el) {
	if (typingElements.has(el)) return;
	typingElements.add(el);
	document.dispatchEvent(new CustomEvent('bean-type-start'));
}
function emitTypingEnd(el) {
	if (!typingElements.has(el)) return;
	typingElements.delete(el);
	document.dispatchEvent(new CustomEvent('bean-type-end'));
}

export function cancelTypeText(el) {
	if (el && typeTimers.has(el)) { clearTimeout(typeTimers.get(el)); typeTimers.delete(el); }
	emitTypingEnd(el);
}

export function typeText(el, text, { delay = 20, jitter = 20, startDelay = 300, onDone } = {}) {
	if (!el) return;
	if (typeTimers.has(el)) clearTimeout(typeTimers.get(el));
	el.textContent = '';
	const cursor = el.nextElementSibling?.classList?.contains('typewriter-cursor') ? el.nextElementSibling : null;
	if (cursor) cursor.style.display = '';
	emitTypingStart(el);
	let i = 0;
	function tick() {
		if (i < text.length) {
			el.textContent += text[i++];
			const t = setTimeout(tick, delay + Math.random() * jitter);
			typeTimers.set(el, t);
		} else {
			emitTypingEnd(el);
			if (cursor) { const t = setTimeout(() => cursor.style.display = 'none', 1500); typeTimers.set(el, t); }
			if (onDone) onDone();
		}
	}
	const t = setTimeout(tick, startDelay);
	typeTimers.set(el, t);
}

// fireFireworks is now re-exported from shared/fireworks.js

/* ─── Bean sparkles (reusable) ──────────── */
const sparkleColors = ['var(--primary)', '#38bdf8', '#fbbf24', '#a78bfa', '#fff'];

export function addSparkles(container, sizeEl, interval) {
	if (!container || !sizeEl) return;
	const pos = getComputedStyle(container).position;
	if (pos === 'static') container.style.position = 'relative';
	throttledInterval(() => {
		// Skip if not visible (check size — hidden elements have 0 dimensions)
		if (container.offsetWidth === 0 && container.offsetHeight === 0) return;
		// Cap max particles per container
		if (container.querySelectorAll('.sparkle').length > 30) return;
		const s = document.createElement('div');
		s.className = 'sparkle';
		const angle = Math.random() * Math.PI * 2;
		const dist = (sizeEl.offsetWidth / 2) + Math.random() * 20;
		const cx = sizeEl.offsetLeft + sizeEl.offsetWidth / 2;
		const cy = sizeEl.offsetTop + sizeEl.offsetHeight / 2;
		s.style.left = (cx + Math.cos(angle) * dist) + 'px';
		s.style.top = (cy + Math.sin(angle) * dist) + 'px';
		s.style.background = sparkleColors[Math.floor(Math.random() * sparkleColors.length)];
		s.style.boxShadow = `0 0 6px ${s.style.background}`;
		s.style.width = (4 + Math.random() * 5) + 'px';
		s.style.height = s.style.width;
		s.style.setProperty('--dx', (Math.random() - 0.5) * 60 + 'px');
		s.style.setProperty('--dy', -(20 + Math.random() * 40) + 'px');
		container.appendChild(s);
		s.addEventListener('animationend', () => s.remove());
	}, interval);
}

/* ─── Rocket exhaust effect (reuses firework particles) ── */
let exhaustInterval = null;

export function startExhaust(beanEl) {
	stopExhaust();
	const container = beanEl;
	const pos = getComputedStyle(container).position;
	if (pos === 'static') container.style.position = 'relative';
	const mascot = beanEl.querySelector('.mascot-small') || beanEl.querySelector('.bean-float-img');
	if (!mascot) return;
	exhaustInterval = throttledInterval(() => {
		for (let i = 0; i < 4; i++) {
			const p = document.createElement('div');
			const isPaw = Math.random() < 0.1;
			p.className = 'firework-particle ' + (isPaw ? 'firework-paw' : 'firework-dot');
			const sz = isPaw ? (8 + Math.random() * 12) : (3 + Math.random() * 6);
			p.style.width = sz + 'px';
			p.style.height = sz + 'px';
			if (!isPaw) p.style.background = EXHAUST_COLORS[Math.floor(Math.random() * EXHAUST_COLORS.length)];
			const cx = mascot.offsetLeft + mascot.offsetWidth / 2;
			const bottom = mascot.offsetTop + mascot.offsetHeight;
			p.style.left = (cx + (Math.random() - 0.5) * 30) + 'px';
			p.style.top = bottom + 'px';
			// Bias downward with slight horizontal spread
			const dx = (Math.random() - 0.5) * 60;
			const dy = 40 + Math.random() * 80;
			p.style.setProperty('--fx', dx + 'px');
			p.style.setProperty('--fy', dy + 'px');
			p.style.setProperty('--fr', (Math.random() * 720 - 360) + 'deg');
			p.style.setProperty('--fw-dur', (0.4 + Math.random() * 0.5) + 's');
			p.style.setProperty('--fw-delay', '0s');
			container.appendChild(p);
			p.addEventListener('animationend', () => p.remove());
		}
	}, 50);
}

export function stopExhaust() {
	if (exhaustInterval) { clearThrottled(exhaustInterval); exhaustInterval = null; }
}

/* ─── Animated blob background ──────────── */
const blobColors = [
	{ r: 232, g: 67, b: 147 },
	{ r: 244, g: 114, b: 182 },
	{ r: 56, g: 189, b: 248 },
	{ r: 168, g: 85, b: 247 },
	{ r: 251, g: 146, b: 60 },
];

export function initBlobBg(canvasId) {
	const canvas = document.getElementById(canvasId);
	if (!canvas) return;
	const ctx = canvas.getContext('2d');
	let w, h, running = true;
	function resize() {
		const parent = canvas.parentElement;
		w = canvas.width = parent.offsetWidth;
		h = canvas.height = parent.offsetHeight;
	}
	resize();
	window.addEventListener('resize', resize);
	// Pause rendering when canvas parent is scrolled out of view
	if (canvasId === 'hero-bg') {
		const hero = canvas.parentElement;
		const obs = new IntersectionObserver(([entry]) => {
			running = entry.isIntersecting;
			if (running) draw();
		}, { threshold: 0 });
		obs.observe(hero);
	}
	const blobs = [];
	for (let i = 0; i < 8; i++) {
		const c = blobColors[i % blobColors.length];
		blobs.push({ x: Math.random() * (w||400), y: Math.random() * (h||400), r: 150 + Math.random() * 250, vx: (Math.random()-0.5)*0.4, vy: (Math.random()-0.5)*0.3, color: c, phase: Math.random()*Math.PI*2 });
	}
	function draw() {
		if (!running) return;
		ctx.clearRect(0, 0, w, h);
		const t = performance.now() * 0.001;
		const isDark = document.documentElement.dataset.theme === 'dark';
		const alpha = isDark ? 0.08 : 0.12;
		for (const b of blobs) {
			b.x += b.vx + Math.sin(t*0.3+b.phase)*0.3;
			b.y += b.vy + Math.cos(t*0.25+b.phase)*0.2;
			if (b.x < -b.r) b.x = w+b.r; if (b.x > w+b.r) b.x = -b.r;
			if (b.y < -b.r) b.y = h+b.r; if (b.y > h+b.r) b.y = -b.r;
			const radius = b.r * (1 + Math.sin(t*0.5+b.phase)*0.15);
			const grad = ctx.createRadialGradient(b.x,b.y,0,b.x,b.y,radius);
			grad.addColorStop(0, `rgba(${b.color.r},${b.color.g},${b.color.b},${alpha})`);
			grad.addColorStop(1, `rgba(${b.color.r},${b.color.g},${b.color.b},0)`);
			ctx.fillStyle = grad;
			ctx.beginPath();
			ctx.arc(b.x, b.y, radius, 0, Math.PI*2);
			ctx.fill();
		}
		requestAnimationFrame(draw);
	}
	draw();
}
